import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MaskLines } from "@/components/motion";
import { useLang } from "@/context/LangContext";
import { usePlayer } from "@/context/PlayerContext";
import { useImg } from "@/lib/design";
import { OnAirBadge } from "@/components/home/OnAirBadge";

const ArchImage = ({ y }) => {
  const heroImg = useImg("hero");
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div
        data-testid="hero-arch-image"
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 0 0)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 0 0)" }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
        className="relative mx-auto h-[380px] w-full max-w-[420px] overflow-hidden rounded-t-[999px] sm:h-[480px] lg:h-[600px]"
      >
        <motion.img style={{ y }} src={heroImg} data-ve-img="hero" alt="Sunrise over the Pacific Ocean" className="absolute inset-0 h-[125%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B111E] via-[#0B111E]/10 to-[#0B111E]/40" />
        <div className="absolute left-1/2 top-[18%] h-[46%] w-[3px] -translate-x-1/2 rounded-full bg-[#F3D27A] shadow-[0_0_30px_6px_rgba(212,175,55,0.55)]" />
        <div className="absolute left-1/2 top-[30%] h-[3px] w-[34%] -translate-x-1/2 rounded-full bg-[#F3D27A] shadow-[0_0_30px_6px_rgba(212,175,55,0.55)]" />
        <div className="absolute inset-x-0 bottom-6 text-center font-mono text-[10px] uppercase tracking-[0.35em] text-[#F4EEE2]/80">Joshua 24:15</div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { playLive } = usePlayer();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-16%"]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden pt-16 md:pt-24">
      <motion.div style={{ opacity: glowOpacity }} className="pointer-events-none absolute -top-40 right-[-10%] h-[700px] w-[700px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.22),transparent_65%)]" />
      <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:px-10 lg:grid-cols-12">
        <motion.div style={{ y: titleY }} className="relative z-10 min-w-0 lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
            <span className="live-dot" /> {t("hero.over")}
          </motion.p>
          <MaskLines
            onLoad
            as="h1"
            lines={[<span key="a" style={{ color: "var(--c-title1, #0A0A0A)" }}>{t("hero.title1")}</span>, <span key="k" className="italic" style={{ color: "var(--c-title2, #C8862F)" }}>{t("hero.title2")}</span>]}
            className="font-display mt-6 text-[clamp(4.2rem,13vw,11.5rem)] font-semibold leading-[0.86] tracking-[-0.03em] text-[#F4EEE2]"
            data-testid="hero-title"
          />
          <MaskLines
            onLoad
            delay={0.35}
            as="p"
            lines={[t("hero.tagline")]}
            className="font-display mt-6 text-2xl italic text-[#F4EEE2]/90 sm:text-3xl"
          />
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8 }} className="mt-6 max-w-lg text-base leading-relaxed text-[#C9D0DB]">
            {t("hero.sub")}
          </motion.p>
          <OnAirBadge />
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1 }} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-listen-live-btn"
              onClick={playLive}
              className="listen-btn group relative flex items-center gap-4 overflow-hidden rounded-full bg-[#D4AF37] py-3 pl-3 pr-8 font-ui text-base font-semibold text-[#0B111E] transition-transform duration-300 hover:-translate-y-0.5"
            >
              <span className="relative grid h-12 w-12 place-items-center rounded-full bg-[#0B111E] text-[#D4AF37]">
                <span className="absolute inset-0 animate-ping rounded-full bg-[#D4AF37]/40" />
                <svg viewBox="0 0 24 24" className="relative h-5 w-5 translate-x-[1px]" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              </span>
              {t("cta.listen")}
            </button>
            <Link to="/sermons" data-testid="hero-explore-btn" className="group flex items-center gap-2 font-ui text-sm font-semibold text-[#F4EEE2] underline-offset-8 hover:underline">
              {t("cta.explore")} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </motion.div>
        <div className="relative lg:col-span-5">
          <ArchImage y={imgY} />
        </div>
      </div>
      <div className="mx-auto mt-10 hidden max-w-[1400px] items-center gap-2 px-10 pb-8 font-mono text-[10px] uppercase tracking-[0.3em] text-[#B7BFCC] md:flex">
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> {t("hero.scroll")}
      </div>
    </section>
  );
};
